import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/** Content-Disposition ヘッダから保存ファイル名を取り出す */
function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8|utf-8)''([^;\n]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* デコード失敗時は次へ */
    }
  }
  const quoted = /filename\s*=\s*"((?:\\.|[^"\\])*)"/i.exec(header);
  if (quoted?.[1]) {
    return quoted[1].replace(/\\(.)/g, '$1');
  }
  const plain = /filename\s*=\s*([^;\n]+)/i.exec(header);
  if (plain?.[1]) {
    return plain[1].trim().replace(/^["']|["']$/g, '');
  }
  return null;
}

/** 保存ファイル名にディレクトリ成分が混ざらないよう単純化 */
export function sanitizeDownloadBasename(name: string): string {
  const base = name.replace(/\\/g, '/').split('/').pop()?.trim() || name.trim();
  return base || 'downloaded_file';
}

/** 拡張子を除いたベース名が UUID 形式か（パス末尾が GUID のみのときのフォールバック判定） */
export function isLikelyUuidFileBase(name: string): boolean {
  const base = name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : name;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(base);
}

/** Blob の MIME からわかりやすい拡張子（フォールバック用） */
function extensionFromMime(mime: string): string {
  const m: Record<string, string> = {
    'text/plain': '.txt',
    'text/csv': '.csv',
    'application/json': '.json',
    'application/octet-stream': '.bin',
  };
  return m[mime] ?? '';
}

/**
 * ファイル参照文字列を API 用の bucketCode と fileSystemId に分解する。
 * `//bucketCd/rest/of/path` のときは bucketCd をクエリ、bucketCd 以降を fileSystemId にする。
 * それ以外は defaultBucket と ref 全体（trim）を返す。
 */
export function parseFileSystemRef(
  ref: string,
  defaultBucket: string,
): { bucketCode: string; fileSystemId: string } {
  const trimmed = ref.trim();
  const m = /^\/\/([^/]+)\/(.+)$/.exec(trimmed);
  if (m) {
    return { bucketCode: m[1], fileSystemId: m[2] };
  }
  return { bucketCode: defaultBucket, fileSystemId: trimmed };
}

export interface TaskItem extends Record<string, any> {
  // Dynamic fields from configuration
}

// GET /tasks のレスポンス（data が行配列の場合）
export interface TaskResponse {
  resultCode: number;
  resultMessage: string | null;
  data: TaskItem[];
}

// GET /tasks のレスポンス（data に savePath 等が含まれる場合）
export interface ConvertTaskResponse {
  resultCode: number;
  resultMessage: string | null;
  data: {
    saveResult: number;
    rowCount: number;
    savePath: string;
  };
}

import {
  CsvTaskConfig,
  FixedTaskConfig,
  JsonToFixedTaskConfig,
  TaskConfig,
  TaskFileResponse,
} from '../models/task-config.model';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiBaseUrl}/tasks`;
  private taskFileUrl = `${environment.apiBaseUrl}/taskFiles`;
  private fileSystemUrl = environment.fileSystemApiUrl;

  /** 既定の taskFileId でタスク結果（行配列）を取得 */
  getTasks(): Observable<TaskResponse> {
    const params = new HttpParams()
      .set('taskFileId', environment.taskFileId)
      .set('bucketCode', environment.bucketCode);

    return this.http.get<TaskResponse>(this.apiUrl, { params });
  }

  /** 指定 taskFileId で変換タスクを実行（レスポンス形式はタスクにより異なる） */
  runConvertTask(taskFileId: string, bucketCode: string): Observable<ConvertTaskResponse> {
    const params = new HttpParams().set('taskFileId', taskFileId).set('bucketCode', bucketCode);

    return this.http.get<ConvertTaskResponse>(this.apiUrl, { params });
  }

  getFileContent(pathRef: string): Observable<TaskItem[]> {
    const { bucketCode, fileSystemId } = parseFileSystemRef(pathRef, environment.bucketCode);
    const params = new HttpParams().set('bucketCode', bucketCode).set('fileSystemId', fileSystemId);

    return this.http.get<TaskItem[]>(this.fileSystemUrl, { params });
  }

  uploadFileContent(pathRef: string, file: File): Observable<any> {
    const { bucketCode, fileSystemId } = parseFileSystemRef(pathRef, environment.bucketCode);
    const params = new HttpParams().set('bucketCode', bucketCode).set('fileSystemId', fileSystemId);

    const formData = new FormData();
    formData.append('formFile', file);

    return this.http.post(this.fileSystemUrl, formData, { params });
  }

  /**
   * ファイルシステムから Blob を取得し、保存時の推奨ファイル名を付与する。
   * パス末尾が人間可読なら従来どおりパス優先（以前の split('/').pop() と同じ）。
   * 末尾が UUID 形式のときだけ Content-Disposition または download+拡張子にフォールバックする。
   */
  downloadFile(pathRef: string): Observable<{ blob: Blob; suggestedFileName: string }> {
    const { bucketCode, fileSystemId } = parseFileSystemRef(pathRef, environment.bucketCode);
    const params = new HttpParams().set('bucketCode', bucketCode).set('fileSystemId', fileSystemId);

    return this.http
      .get(this.fileSystemUrl, { params, observe: 'response', responseType: 'blob' })
      .pipe(
        map((res) => {
          const body = res.body;
          if (!body) {
            throw new Error('Empty response body');
          }

          const fromPathRaw = fileSystemId.split('/').pop()?.trim() ?? '';
          const pathBasename = sanitizeDownloadBasename(fromPathRaw || 'downloaded_file');

          const cdHeader = res.headers.get('content-disposition');
          const fromHeader = parseFilenameFromContentDisposition(cdHeader);
          const headerBasename = fromHeader ? sanitizeDownloadBasename(fromHeader) : '';

          let suggestedFileName: string;
          if (fromPathRaw && !isLikelyUuidFileBase(pathBasename)) {
            // 従来挙動: seino/input/delivery_data.txt → delivery_data.txt
            suggestedFileName = pathBasename;
          } else if (headerBasename && !isLikelyUuidFileBase(headerBasename)) {
            // パスが GUID のみのとき、サーバが付けた分かりやすい名前を採用
            suggestedFileName = headerBasename;
          } else if (isLikelyUuidFileBase(pathBasename)) {
            const ext = extensionFromMime(body.type);
            suggestedFileName = `download${ext}`;
          } else {
            suggestedFileName = pathBasename;
          }

          return { blob: body, suggestedFileName };
        }),
      );
  }

  getRawFileContent(pathRef: string): Observable<string> {
    const { bucketCode, fileSystemId } = parseFileSystemRef(pathRef, environment.bucketCode);
    const params = new HttpParams().set('bucketCode', bucketCode).set('fileSystemId', fileSystemId);

    return this.http.get(this.fileSystemUrl, { params, responseType: 'text' });
  }

  /**
   * JSON 表示向けに「パースせず」テキストとして取得する。
   * - `getFileContent()`（JSON→object）と違い、巨大 JSON でも stringify し直さずに扱える。
   * - 大きな内容はメモリに全量保持する点は変わらないため、UI 側でプレビュー表示などの工夫が必要。
   */
  getFileText(pathRef: string): Observable<string> {
    return this.getRawFileContent(pathRef);
  }

  /**
   * 指定文字コードでテキストを取得する（例: Shift_JIS 固定長ファイルの表示）。
   * - ブラウザの TextDecoder が対応しているエンコーディングのみ利用可能。
   * - 大きなファイルは全量をメモリに載せるため注意。
   */
  getRawFileContentDecoded(pathRef: string, encoding: string): Observable<string> {
    const { bucketCode, fileSystemId } = parseFileSystemRef(pathRef, environment.bucketCode);
    const params = new HttpParams().set('bucketCode', bucketCode).set('fileSystemId', fileSystemId);

    return this.http
      .get(this.fileSystemUrl, { params, responseType: 'arraybuffer' })
      .pipe(map((buf) => new TextDecoder(encoding).decode(buf)));
  }

  postTaskFile(config: TaskConfig | FixedTaskConfig | JsonToFixedTaskConfig): Observable<any> {
    const params = new HttpParams().set('wbsTaskId', config.id);
    return this.http.post(this.taskFileUrl, config, { params });
  }

  postCsvTaskFile(config: CsvTaskConfig, wbsTaskId?: string): Observable<any> {
    const taskId = wbsTaskId || config.id;
    const params = new HttpParams().set('wbsTaskId', taskId);
    return this.http.post(this.taskFileUrl, config, { params });
  }

  getTaskFile(wbsTaskId: string): Observable<TaskFileResponse> {
    const params = new HttpParams().set('wbsTaskId', wbsTaskId);
    return this.http.get<TaskFileResponse>(this.taskFileUrl, { params });
  }
}
